import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TOKEN_BUNDLES, { findBundleById, findBundleByTokens } from '../constants/tokenBundles';
import { useCheckout } from '../features/checkout';
import { useAuth } from '../features/auth';
import { useToast } from '../features/toast';
import { auth } from '../services/firebase';

const TokenPurchasePanel = ({ onClose, demoMode = false, onBundleSelect }) => {
  const { tokenCheckoutContext, beginTokenCheckout, dismissTokenCheckout } = useCheckout();
  const { resendVerification } = useAuth();
  const { showToast } = useToast();
  const [view, setView] = useState('select');
  // Filter out first-spin bundles for default selection
  const regularBundles = useMemo(
    () => TOKEN_BUNDLES.filter((bundle) => !bundle.id.startsWith('first-spin-')),
    []
  );
  const [selectedBundleId, setSelectedBundleId] = useState(regularBundles[0]?.id || null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const selectedBundle = useMemo(
    () => (selectedBundleId && findBundleById(selectedBundleId)) || regularBundles[0],
    [selectedBundleId, regularBundles],
  );
  const resolvedSuccessBundle = useMemo(() => {
    return (
      tokenCheckoutContext?.purchase?.bundleId &&
        findBundleById(tokenCheckoutContext.purchase.bundleId)) ||
      (tokenCheckoutContext?.bundleId && findBundleById(tokenCheckoutContext.bundleId)) ||
      (tokenCheckoutContext?.purchase?.tokens &&
        findBundleByTokens(tokenCheckoutContext.purchase.tokens)) ||
      null;
  }, [tokenCheckoutContext]);

  useEffect(() => {
    if (!tokenCheckoutContext) {
      return;
    }
    setIsCheckoutLoading(false);
    if (tokenCheckoutContext.status === 'pending') {
      setView('processing');
      return;
    }
    if (tokenCheckoutContext.status === 'success') {
      setView('success');
      return;
    }
    if (tokenCheckoutContext.status === 'cancel') {
      setView('cancel');
      return;
    }
    if (tokenCheckoutContext.status === 'error') {
      setView('error');
      return;
    }
  }, [tokenCheckoutContext]);

  const disableSelection = view === 'processing' || isCheckoutLoading;

  const handleSelectBundle = useCallback((bundle) => {
    if (!bundle || !bundle.id || disableSelection) return;
    setSelectedBundleId(bundle.id);
    setView('select');
    // DEMO MODE: Call onBundleSelect when any bundle is selected
    if (demoMode && onBundleSelect) {
      onBundleSelect();
    }
  }, [disableSelection, demoMode, onBundleSelect]);

  const handleCheckout = useCallback(async () => {
    if (!selectedBundle || disableSelection) return;
    
    // DEMO MODE: Don't allow checkout
    if (demoMode) {
      return;
    }
    
    // CHANGED: use toast to block action for unauthenticated or unverified users
    const currentUser = auth.currentUser;
    if (!currentUser) {
      showToast({ message: 'Sign in to purchase tokens.', tone: 'info' });
      return;
    }
    try { await currentUser.reload(); } catch (_) {}
    if (!auth.currentUser?.emailVerified) {
      // CHANGED: match legacy wording and include Resend link
      showToast({
        message: (
          <>
            Verify your email to unlock all features.&nbsp;
            <button
              type="button"
              onClick={() => { try { resendVerification?.(); } catch (_) {} }}
              className="font-semibold underline underline-offset-2 hover:opacity-80"
            >
              Resend.
            </button>
          </>
        ),
        tone: 'info',
      });
      return;
    }
    setIsCheckoutLoading(true);
    setView('processing');
    try {
      await beginTokenCheckout(selectedBundle);
    } catch (err) {
      setIsCheckoutLoading(false);
      setView('error');
    }
  }, [selectedBundle, disableSelection, beginTokenCheckout, showToast, resendVerification, demoMode]);

  const handleClose = useCallback(() => {
    dismissTokenCheckout();
    onClose?.();
    setView('select');
    setIsCheckoutLoading(false);
  }, [dismissTokenCheckout, onClose]);
  const renderSelectView = () => (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-ponpon-navy">Top up your tokens</h4>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="rounded-full border border-ponpon-navy/15 bg-white px-2 py-1 text-xs font-semibold text-ponpon-navy/70 transition hover:border-ponpon-coral/40 hover:text-ponpon-coral"
        >
          Close
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {TOKEN_BUNDLES.filter((bundle) => !bundle.id.startsWith('first-spin-')).map((bundle) => {
          const isActive =
            selectedBundleId ? selectedBundleId === bundle.id : bundle.id === selectedBundle.id;
          return (
            <button
              type="button"
              key={bundle.id}
              onClick={() => {
                handleSelectBundle(bundle);
              }}
              disabled={disableSelection}
              className={[
                'group flex items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm text-ponpon-navy shadow-sm transition',
                isActive
                  ? 'border-ponpon-coral/60 bg-ponpon-sky/20 text-ponpon-coral'
                  : 'border-ponpon-navy/10 bg-white hover:border-ponpon-coral/50 hover:text-ponpon-coral',
                disableSelection ? 'opacity-70' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="font-semibold">
                {bundle.tokens} token{bundle.tokens === 1 ? '' : 's'}
              </span>
              {!demoMode && (
                <span className="text-sm font-semibold text-ponpon-coral group-hover:text-[#E23A32]">
                  ${bundle.price}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-2">
        {demoMode ? (
          <p className="px-1 text-[0.6rem] leading-snug text-ponpon-navy/60">
            In demo mode, you'll receive demo tokens to experience the spin.
          </p>
        ) : (
          <>
            <button
              type="button"
              disabled={!selectedBundle || disableSelection}
              onClick={handleCheckout}
              className={[
                'flex items-center justify-center rounded-full bg-ponpon-coral px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#E23A32]',
                (!selectedBundle || disableSelection) && 'cursor-not-allowed opacity-70 hover:bg-ponpon-coral',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isCheckoutLoading || view === 'processing' ? 'Redirecting…' : 'Checkout'}
            </button>
            <p className="px-1 text-[0.6rem] leading-snug text-ponpon-navy/60">
              Secure checkout is powered by Stripe. Your tokens will be added to your balance once the
              payment succeeds.
            </p>
          </>
        )}
      </div>
    </>
  );

  const renderSuccessView = () => (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-ponpon-navy">Tokens added!</h4>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="rounded-full border border-ponpon-navy/15 bg-white px-2 py-1 text-xs font-semibold text-ponpon-navy/70 transition hover:border-ponpon-coral/40 hover:text-ponpon-coral"
        >
          Close
        </button>
      </div>
      <div className="flex flex-col items-center gap-2.5 rounded-2xl border border-ponpon-coral/30 bg-ponpon-sky/20 px-3.5 py-5 text-center text-ponpon-navy">
        <div className="text-4xl" aria-hidden="true">
          🎉
        </div>
        <span className="text-sm font-semibold uppercase tracking-wide text-ponpon-coral/70">
          Success
        </span>
        {resolvedSuccessBundle ? (
          <p className="text-xs text-ponpon-navy/70">
            {resolvedSuccessBundle.tokens} token
            {resolvedSuccessBundle.tokens === 1 ? '' : 's'} added for ${resolvedSuccessBundle.price}.
          </p>
        ) : null}
      </div>
    </>
  );

  const renderStatusView = ({ title, message, actionLabel }) => (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-ponpon-navy">{title}</h4>
          <p className="text-xs text-ponpon-navy/65">{message}</p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="rounded-full border border-ponpon-navy/15 bg-white px-2 py-1 text-xs font-semibold text-ponpon-navy/70 transition hover:border-ponpon-coral/40 hover:text-ponpon-coral"
        >
          Close
        </button>
      </div>
      <div className="flex flex-col items-center gap-2.5 rounded-2xl border border-ponpon-navy/15 bg-white px-3.5 py-4 text-sm text-ponpon-navy/75 shadow-sm">
        <div className="text-2xl" aria-hidden="true">
          {view === 'processing' ? '⏳' : view === 'cancel' ? '🛑' : '⚠️'}
        </div>
        <p className="text-xs text-ponpon-navy/70 text-center">{actionLabel}</p>
      </div>
      {/* Removed Try again button to match original design; user can close and reopen */}
    </>
  );

  let content = null;
  if (view === 'success') {
    content = renderSuccessView();
  } else if (view === 'cancel') {
    content = renderStatusView({
      title: 'Checkout cancelled',
      message:
        tokenCheckoutContext?.message ||
        'Something interrupted your payment. You can retry or switch payment methods.',
      actionLabel: 'No tokens were charged. Pick a bundle again whenever you are ready.',
    });
  } else if (view === 'error') {
    content = renderStatusView({
      title: 'Checkout unavailable',
      message:
        tokenCheckoutContext?.message ||
        'We could not start the payment flow. Please try again or contact support.',
      actionLabel: 'It looks like we had trouble creating a checkout session.',
    });
  } else if (view === 'processing') {
    content = renderStatusView({
      title: 'Redirecting to checkout…',
      message: 'One moment while we prepare your secure session.',
      actionLabel: 'If nothing happens after a few seconds, you can refresh or try again.',
    });
  } else {
    content = renderSelectView();
  }

  return (
    <div>
      <div className="flex w-72 flex-col gap-3 rounded-2xl border border-white/70 bg-white p-4 shadow-xl">
        {content}
      </div>
    </div>
  );
};

export default TokenPurchasePanel;
